import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { IMqttMessage } from "ngx-mqtt";
import { Observable, of, Subscription, throwError } from "rxjs";
import {
  debounceTime,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { DataStorage } from "src/model/class/general classes/data-storage";
import {
  BalloonIcon,
  NotificationObjectKind,
  NotificationObjectKindGroup,
} from "src/model/enum/enumNotifications";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { Notific } from "src/model/class/general classes/notific";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import {
  notificationDeleteAction,
  notificationInsertAction,
  notificationLoadInitStartAction,
  notificationUpdateAction,
} from "src/state/actions/notification-actions";
import { listNotification } from "src/state/selectors/notifications.selectors";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import {
  NotificationRequest as NotificationRequest,
  NotificationRequest_PageJump,
  NotificationRequest_PlayAudio,
  NotificationRequest_ShowBalloon,
  NotificationRequest_ShowPopup,
} from "src/model/class/general classes/notification-request";
import { ToastService } from "./toast.service";
import { ToastType } from "src/components/shared-components/enum/enumMisc";
import { PopupService } from "./popup.service";
import { LogicsService } from "./logics.service";
import { SecuretyDataProtectionService } from "./securety-data-protection.service";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { MqttClientService } from "./mqtt-client.service";
import { Router } from "@angular/router";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { serverSettings } from "src/state/selectors/server-settings-selector";
import { Settings } from "src/model/class/settings";
import { LogicLog } from "src/model/class/general classes/logic-log";
import { BIGLogicLogState } from "src/components/shared-components/enum/enumLogic";
import { SystemRouterService } from "./system-router.service";
import { clientSettings } from "src/state/selectors/client-settings.selectors";
import { ClientSettings } from "src/model/class/client-settings";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { GenericUserData } from "src/model/interface/generic-user-data";

@Injectable({
  providedIn: "root",
})
export class NotificationService
  extends DataStorage
  implements OnDestroy, OnInit, iEvent, iMqttHandling {
  private _logContext: string = "Notification.service";

  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  _eventSubscriptions: Array<Subscription> = null;

  private _selectCurrentUserSub: Subscription = null;
  private _currentUser: CurrentSessionData = null;
  private _isUserAuthenticated: boolean = false;

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectServerSettingsUserSub: Subscription = null;
  private _selectClientSettingsSub: Subscription = null;

  private _audioNotificId: Map<number, HTMLAudioElement> = new Map<
    number,
    HTMLAudioElement
  >();
  private _timeoutAudio: Map<number, any> = new Map<number, any>();

  private _gridVisible: boolean = false;

  private _clientSettings: ClientSettings = null;
  constructor(
    private _http: HttpClient,
    private _eventService: EventService,
    private _mqttService: MqttClientService,
    private _loggerService: LoggerService,
    private _toastService: ToastService,
    private _store: Store,
    private _popupService: PopupService,
    private _securetyDataProtectionService: SecuretyDataProtectionService,
    private _logicsService: LogicsService,
    private _router: Router,
    private _systemRouterService: SystemRouterService
  ) {
    super();

    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    this.ngOnInit();
  }

  ngOnInit() {
    /** Subscrive event from Event Service */
    this.loadCurrentUser();
    this.subscribeEvents();
    this.subscribeMQTTAddresses();
    this.subscribeToSeverSettings();
    //this.loadCurrentUser();
  }

  /**
   *  Destroy all observable service subject
   */
  ngOnDestroy() {
    this.unsubscribeEvents();
    this.unsubscribeStoreSelect();
    this.unsubscribeToMQTTAddresses();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this.subscribeMQTTAddresses();
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {

        this.stopAudio(-1);
        await this.unsubscribeToMQTTAddresses();
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this.stopAudio(-1);
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Initialize public observable list trasmission: it should be call in the costructor of all component/page that you need.
   */
  public Init(): void {
    this._store.dispatch(notificationLoadInitStartAction());
  }

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData(): void {
    this.Init();
  }

  //#region Mqtt-addresses handling

  public async subscribeMQTTAddresses(): Promise<void> {
    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    let mqttAddressSubcriptionNotificationRequest: Subscription = null;
    let returnedObservableNotificationRequest: Observable<IMqttMessage> = null;

    let mqttAddressSubscription: Subscription = null;
    let returnedObservableAlarm: Observable<IMqttMessage> = null;

    let mqttAddressSubcriptionSolveLater: Subscription = null;
    let returnedObservableSolveLater: Observable<IMqttMessage> = null;

    //#region Subscribing to RESERVED_NOTIFICATION_REQUEST to handle request of show notification
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_NOTIFICATION_REQUEST;

    returnedObservableNotificationRequest =
      await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcriptionNotificationRequest =
      returnedObservableNotificationRequest
        .pipe(
          map((mqttMessage: IMqttMessage) => {
            return JSON.parse(
              mqttMessage.payload.toString()
            ) as NotificationRequest;
          })
        )
        .subscribe((notificationRequested: NotificationRequest) => {
          this.HandleNotification(notificationRequested);
        });

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcriptionNotificationRequest
    );
    //#endregion

    //#region

    //#endregion
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_ALARM_SOLVELATER;

    returnedObservableSolveLater = await this._mqttService.subscribeTopic(
      mqttAddress
    );
    mqttAddressSubcriptionSolveLater = returnedObservableSolveLater
      .pipe(
        map((mqttMessage: IMqttMessage) => {
          return JSON.parse(mqttMessage.payload.toString());
        })
      )
      .subscribe(async (message: any) => {
        this.sendNotification(Number(message.Message.Value), 1);
      });
    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcriptionSolveLater
    );
    //#region

    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_ALARM;
    returnedObservableAlarm = await this._mqttService.subscribeTopic(
      mqttAddress
    );
    mqttAddressSubscription = returnedObservableAlarm
      .pipe(
        map((mqttMessage: IMqttMessage) => {
          return JSON.parse(mqttMessage.payload.toString());
        })
      )
      .subscribe(async (message: any) => {
        this.sendNotification(Number(message.Message.Value), 2);
      });
    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubscription
    );
    //#endregion

    //#region Subscribing to RESERVED_DB_NOTIFICATION_CHANGED
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_NOTIFICATION_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        withLatestFrom(this._store.select(listNotification)),
        filter((combinedObject: Array<any>) => combinedObject[1] !== null),
        map((combinedObject: Array<any>) => combinedObject[0]),
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        }),
        switchMap(async (message: Array<string>) => {
          /** map operator allow to make parallel execution without cancelling event: when this fired multiple times
           * it perform execeution without waiting previous execution complete
           */
          if (message.length === 3) {
            const operationKind = parseInt(message[0]);
            const idNotification = parseInt(message[1]);
            const clientID = message[2];

            /** Finding operation kind: Insert, Update, Delete */

            if (operationKind === GenericOperationKind.INSERT) {
              this._store.dispatch(
                notificationInsertAction({ idNotification })
              );
            } else if (operationKind === GenericOperationKind.UPDATE) {
              this._store.dispatch(
                notificationUpdateAction({ idNotification })
              );
            } else if (operationKind === GenericOperationKind.DELETE) {
              this._store.dispatch(
                notificationDeleteAction({ idNotification })
              );
            }
          } else {
            throw new Error(
              "OnNotificationDataChange - Incorrect format message"
            );
          }

          return message;
        }),
        debounceTime(1000)
      )
      .subscribe(
        (message: Array<string>) => {
          this._eventService.OnNotificationsChange.next(message);
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeAddress",
            "AppReserved.RESERVED_DB_NOTIFICATION_CHANGED",
            "Error: " + error.message
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );

    //#endregion
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();
      /*
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              this._logContext,
              "checkLocalAddressSubscriptionMap()",
              "Address ALREADY present " + addressToCheck
            );
            */
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );
      /*
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              this._logContext,
              "checkLocalAddressSubscriptionMap()",
              "NEW address: " + addressToCheck
            );
            */
      result = true;
    }

    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  //#endregion

  //#region Notification API operations

  /**
   * Get full list of notification
   *
   * @returns
   */
  public getNotifications(): Observable<GenericApiData> {
    return this._http.get<GenericApiData>(
      environment.httpServerRoot + AppSettings.API_URL_NOTIFICATION
    );
  }

  /**
   * Get last modifed notification
   *
   * @param notificationStore Notification store
   * @returns
   */
  public getNotificationByLastModified(
    notificationStore: GenericApiData
  ): Observable<GenericApiData> {
    const lastAPIModifiedString: string = (notificationStore?.lastAPIModified || "");

    const queryString: string =
      "?lastModified=" + encodeURIComponent(lastAPIModifiedString);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_NOTIFICATION +
        queryString
      )
      .pipe(
        map((returnNotificationStore: GenericApiData) => {
          //if initial resource isn't changed it return null value (returnNotificationStore = null)
          if (returnNotificationStore) {
            notificationStore = returnNotificationStore;
          }

          return notificationStore;
        })
      );
  }

  /**
   * Get single notification by ID from API.
   *  If api call fallback in error costructor, it propagate an Observable notification with .error() function
   */
  public getNotificationByID(idNotification: number): Observable<Notific> {
    const queryString: string = "?id=" + idNotification;

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_NOTIFICATION +
        queryString
      )
      .pipe(
        switchMap((returnNotificationList: GenericApiData) => {
          if (returnNotificationList.data.length > 0) {
            const returnNotification: Notific = returnNotificationList.data[0];
            return of(returnNotification);
          } else {
            return throwError(new Error());
          }
        })
      );
  }
  //#endregion

  //#region  Assign/Disassign notification to generic object
  /**
   *
   * @param idNotification id Current notification to assign to generic object
   * @param idGenericObject id Generic object to assign notification
   * @param objectKind notification object kind to assign notification
   *
   * Assign notification to generic object by object notificatio kind
   *
   */
  AssignNotificationToGenerigObject(
    idNotification: number,
    idGenericObject: number,
    objectKind: NotificationObjectKind
  ): Observable<any> {
    const bodyRequest = {
      IdsNotification: [idNotification],
      NotificationKind: objectKind,
    };

    return this._http.post(
      environment.httpServerRoot +
      AppSettings.API_URL_NOTIFICATION +
      "/" +
      idGenericObject +
      "/assign",
      bodyRequest
    );
  }

  /**
   *
   * @param idNotification id Current notification to disassign to generic object
   * @param idGenericObject id Generic object to disassign notification
   * @param objectKind notification object kind to disassign notification
   *
   * Disassign notification to generic object by object notificatio kind
   *
   */
  public deleteAssignNotificationToGenericObject(
    idNotification: number,
    idGenericObject: number,
    objectKind: NotificationObjectKind
  ): Observable<any> {
    const bodyRequest = {
      body: {
        idsNotification: [idNotification],
        NotificationKind: objectKind,
      },
    };

    return this._http.request(
      "delete",
      environment.httpServerRoot +
      AppSettings.API_URL_NOTIFICATION +
      "/" +
      idGenericObject +
      "/delete",
      bodyRequest
    );
  }
  //#endregion

  //#region HandlingNotification
  /**
   * Receive the request of show a notification and build the correct object
   * Check if the current user must receive that notification
   * @param Notification
   */
  private HandleNotification(Notification: NotificationRequest) {
    const userBallon: string[] = Notification.Balloon.UsersEnabled.split(";");

    //#region Handle "Baloon" type Notification
    if (
      Notification.Balloon.IsEnable &&
      this._isUserAuthenticated &&
      userBallon.includes(this._currentUser.id.toString())
    ) {
      let type: ToastType = ToastType.ERROR;
      let iconName: string = "";
      let text: string = Notification.Balloon.Title;
      if (Notification.Balloon.Text.trim() != "") {
        text += ": " + Notification.Balloon.Text;
      }

      switch (Notification.Balloon.Icon) {
        case BalloonIcon.None:
          type = ToastType.INFO;
          break;
        case BalloonIcon.Info:
          type = ToastType.INFO;
          iconName = "alert-circle";
          break;
        case BalloonIcon.Warning:
          type = ToastType.WARNING;
          iconName = "warning";
          break;
        case BalloonIcon.Error:
          type = ToastType.ERROR;
          iconName = "close-circle";
          break;
      }

      //Audio per i balloon
      this._toastService.showToast(
        type,
        text,
        Notification.Balloon.Timeout * 1000,
        iconName
      );
    }
    //#endregion

    //#region  Handle "Popup" type Notification
    // Handled only the popup about alarms/logics, not the access popups
    const userPopup: string[] = Notification.Popup.UsersEnabled.split(";");
    if (
      Notification.Popup.IsEnable &&
      this._isUserAuthenticated &&
      userPopup.includes(this._currentUser.id.toString())
    ) {
      this._popupService
        .showModalAlarm(
          Notification.Popup,
          Notification.ObjectKind,
          this._gridVisible
        )
        .then((operation) => {
          if (
            operation &&
            (operation.operation == "VIEW" || operation.operation == "RESET")
          ) {
            if (this._isUserAuthenticated) {
              const usernameDecrypted =
                this._securetyDataProtectionService.decypt(
                  this._currentUser.username,
                  null
                );
              if (operation.operation == "VIEW") {
                this._logicsService.ViewAlarm(
                  operation.idLogic,
                  usernameDecrypted
                );
              } else if (operation.operation == "RESET") {
                this.stopAudio(Notification.ObjectID);
                this._logicsService.SolveAlarm(
                  operation.idLogic,
                  usernameDecrypted
                );
              }
            }
          }
        });
    } else {
      const modalPresent = document.getElementsByTagName("ion-modal")[0];
      if (modalPresent) {
        this._popupService
          .showModalAlarm(
            Notification.Popup,
            Notification.ObjectKind,
            this._gridVisible
          )
          .then(() => { }); //
      }
    }
    //#endregion

    //#region Handle "Sound" type Notification
    const userAudio: string[] = Notification.Audio.UsersEnabled.split(";");
    if (Notification.Audio.solveLater) this.stopAudio(-1);

    if (
      Notification.Audio.IsEnable &&
      this._isUserAuthenticated &&
      userAudio.includes(this._currentUser.id.toString())
    ) {
      this.stopAudio(-1);
      if (Notification.Audio.Audio != -1) {
        this.loadPlayAudio(Notification);
      }
    } else {
      if (
        !Notification.Balloon.IsEnable &&
        !Notification.Popup.IsEnable &&
        !Notification.PageJump.IsEnable
      ) {
        this.stopAudio(Notification.ObjectID);
      }
    }
    //#endregion


    //#region Handle "Page-Jump" type Notification
    const userPageJump: string[] =
      Notification.PageJump.UsersEnabled.split(";");

    if (
      Notification.PageJump.IsEnable &&
      this._isUserAuthenticated &&
      userPageJump.includes(this._currentUser.id.toString())
    ) {
      // Only if I'm in supervisions/ or list/ pages jump to the page
      if (
        this._router.url.includes("/supervisions") ||
        this._router.url.includes("/list")
      ) {
        if (this._clientSettings.supervisionListViewMode) {
          this._systemRouterService.navigateToSupervisionList(
            Notification.PageJump.PageID,
            Notification.PageJump.EnvironmentID
          );
        } else {
          this._systemRouterService.navigateToSupervisionGraph(
            Notification.PageJump.PageID,
            Notification.PageJump.EnvironmentID
          );
        }
      }
    }
    //#endregion

  }
  //#endregion

  /**
   * Subscribe to settings about what informations we need to show in popup alarm
   */
  private subscribeToSeverSettings(): void {
    this._selectServerSettingsUserSub = this._store
      .select(serverSettings)
      .subscribe((settings: Settings) => {
        if (settings != null) {
          this._gridVisible =
            String(
              settings.Notification_ShowGridIntoNotificationPopupWindow
            ).toLowerCase() == "true";
        }
      });

    this._selectClientSettingsSub = this._store
      .select(clientSettings)
      .pipe(filter((clientSetting) => clientSetting !== null))
      .subscribe((clientSettings: ClientSettings) => {
        this._clientSettings = clientSettings;
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._selectCurrentUserSub?.unsubscribe();
    this._selectServerSettingsUserSub?.unsubscribe();
    this._selectClientSettingsSub?.unsubscribe();
  }
  //#endregion

  /**
   * Loads the necessary audio file and then plays it
   * a certain number of times based on the values in Notification.Audio.
   * @param notification Notification with audio
   */
  private async loadPlayAudio(notification: NotificationRequest) {
    let audio = new Audio();
    audio.src =
      environment.httpServerRoot +
      environment.apiFolderDownloads +
      "Audio_" +
      notification.Audio.Audio +
      ".mp3";
    if (isNaN(audio.duration)) {
      audio.src =
        environment.httpServerRoot +
        environment.apiFolderDownloads +
        "Audio_" +
        notification.Audio.Audio +
        ".wav";
    }
    if (typeof audio.duration === "number") {
      audio.loop = notification.Audio.RepeatUntilSolution;
      audio.load();
      this._audioNotificId.set(notification.ObjectID, audio);

      let timer = null;
      this._audioNotificId.get(notification.ObjectID).play();

      let value = 0;
      if (!this._audioNotificId.get(notification.ObjectID).loop) {
        let interval =
          notification.Audio.Interval > 0 ? notification.Audio.Interval : 1;

        timer = setInterval(() => {
          if (value == 0) {
            this._timeoutAudio.set(notification.ObjectID, timer);
          }

          if (value < notification.Audio.Repeat && this._audioNotificId.has(notification.ObjectID)) {
            this._audioNotificId.get(notification.ObjectID).play();
          }

          if (
            value >= notification.Audio.Repeat ||
            this._timeoutAudio.get(notification.ObjectID) == null ||
            this._audioNotificId.get(notification.ObjectID) == null
          ) {
            clearInterval(timer);
            timer = null;
          }//
          value++;
        }, interval * 1000);
      }
    }
  }

  /**
   * Stop the audio that is in the id position in the array _audioNotificId
   * @param id location to search for audio
   */
  private stopAudio(id: number) {
    if (id == -1) {
      this._audioNotificId.forEach((value, key, map) => {
        this._audioNotificId.get(key).pause();
        this._audioNotificId.get(key).currentTime = 0;
        this._audioNotificId.delete(key);
      });
      this._timeoutAudio.forEach((value, key, map) => {
        clearInterval(this._timeoutAudio.get(key));
        this._timeoutAudio.delete(key);
      });
    } else {
      if (this._audioNotificId.get(id)) {
        this._audioNotificId.get(id).pause();
        this._audioNotificId.get(id).currentTime = 0;
        this._audioNotificId.delete(id);
      }
      if (this._timeoutAudio.get(id)) {
        clearInterval(this._timeoutAudio.get(id));
        this._timeoutAudio.delete(id);
      }
    }
  }

  /**
   * Create a notification and send it.
   * @param id alarm id
   * @param value 1 -> reset_later -> stop audio
   */
  private async sendNotification(id: number, value: number): Promise<void> {
    let logicsLog = await this._logicsService.GetActiveAlarms(true, id);
    if (logicsLog) {
      let fromLogicToNotification: LogicLog = null;
      let notification: NotificationRequest = new NotificationRequest();
      if (logicsLog instanceof Array) {
        fromLogicToNotification = logicsLog[0];
      } else {
        fromLogicToNotification = logicsLog;
      }
      if (fromLogicToNotification.state == BIGLogicLogState.ToSolveLater) {
        notification.ObjectGroup = NotificationObjectKindGroup.Logic;
        notification.ObjectID = fromLogicToNotification.idbigLogic;
        notification.ObjectKind = NotificationObjectKind.AlarmTrue;
        notification.Popup = new NotificationRequest_ShowPopup();
        notification.Popup.Closable = false;
        notification.Popup.ImageID = -1;
        notification.Popup.IsEnable = true;
        notification.Popup.Text = "";
        notification.Popup.Title = fromLogicToNotification.description;
        notification.Audio = new NotificationRequest_PlayAudio();
        if (value == 1) {
          let audioNotific = await this._logicsService.GetActiveAlarms(
            false,
            id
          );
          if (audioNotific) {
            notification.Audio.Audio = -1;
            notification.Audio.Interval = 0;
            notification.Audio.IsEnable = true;
            notification.Audio.Repeat = 0;
            notification.Audio.RepeatUntilSolution = false;
            notification.Audio.solveLater = true;
          }
        }
        notification.Balloon = new NotificationRequest_ShowBalloon();
        notification.PageJump = new NotificationRequest_PageJump();

        this.HandleNotification(notification);
      }
    }
  }

  /**
   * Load current user data
   */
  private loadCurrentUser(): void {

    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => currentUserData.currentUser),
      )
      .subscribe((currentUser: CurrentSessionData) => {
        this._currentUser = currentUser;

        if (currentUser) {
          this._isUserAuthenticated = !!currentUser?.accessToken;
        } else {
          this._isUserAuthenticated = false;
        }
      });
  }
}
